import { Box, theme } from '@components/gassan-ui'
import { useLocalization } from '@components/Localization'
import { useNotifications } from '@components/NotificationsProvider'
import { useWishlist } from '@components/WishlistProvider'
import { useAddArticleToCartMutation } from '@generated'
import { ARTICLES_BY_ID } from '@graphql/articlesById'
import { ArticlesByID, ArticlesByIDVariables } from '@graphql/__generated__/ArticlesByID'
import { useCart } from '@lib/hooks/useCart'
import { imgix } from '@lib/imigx'
import { useRouter } from 'next/router'
import React, { FC, useEffect, useState } from 'react'
import * as ReactGA from 'react-ga'
import { useQuery } from 'urql'
import EmptyWishlist from './EmptyWishlist'
import Row from './Row'
import LoadingRow from './Row/LoadingRow'

const WishlistList: FC = () => {
  const { lang, messages } = useLocalization()
  const { wishlistProductIds, toggleWishlistProductId } = useWishlist()
  const notifications = useNotifications()
  const cart = useCart()
  const router = useRouter()
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [result] = useQuery<ArticlesByID, ArticlesByIDVariables & { isDataLoaded: boolean }>({
    query: ARTICLES_BY_ID,
    variables: { ids: wishlistProductIds, isDataLoaded },
    pause: isDataLoaded
  })

  const [, addArticleToCart] = useAddArticleToCartMutation()

  useEffect(() => {
    result.data && setIsDataLoaded(true)
  }, [result.data])

  const handleAddArticleToCart = async (articleId: string) => {
    try {
      await addArticleToCart({ reference: cart.id, articleId, quantity: 1 }).then(data => {
        const article = result.data.articlesById?.find(item => item.id === articleId)
        const prefix = lang === 'nl' ? '/nl' : '/en'
        notifications.addNotification({
          variant: 'image-action',
          label: article.brand,
          sublabel: article.description,
          image: imgix(article.images[0], { width: 180 }),
          description: lang === 'nl' ? 'Toegevoegd aan winkelmand' : 'Added to shopping cart',
          actionLabel: lang === 'nl' ? 'Bekijk' : 'View',
          onClickAction: () =>
            router.push(
              `/[lang]/[...slug]`,
              `${prefix}${messages.navigation.secondaryNav.cart.url}`
            )
        })
        ReactGA.event({
          category: 'Ecommerce',
          action: 'addToCart'
        })
      })
    } catch (error) {
      notifications.addNotification({
        variant: 'error',
        label: messages.notifications.refreshAndTryAgain
      })
    }
  }

  return (
    <Box
      pt={{ _: '1rem', small: '1.75rem', xlarge: '2rem' }}
      borderTop={`1px solid ${theme.colors.shades[200]}`}
    >
      {result.fetching ? (
        [...Array(3)].map((_, key) => <LoadingRow key={key} />)
      ) : result.data && result.data.articlesById && wishlistProductIds.length > 0 ? (
        wishlistProductIds.map(id => {
          const item = result.data.articlesById.find(article => article.id === id)
          return (
            <Row
              key={item.id}
              item={item}
              onRemove={() =>
                toggleWishlistProductId({
                  id: item.id,
                  title: item.brand,
                  subtitle: item.description,
                  image: item.images[0]
                })
              }
              onAddToCart={(id: string) => handleAddArticleToCart(id)}
            />
          )
        })
      ) : (
        <EmptyWishlist />
      )}
    </Box>
  )
}

export default WishlistList
